import {
  MetricDependency,
  Metric,
  subjectIsFragment,
  subjectIsGeography,
  combineMetricsForFragments,
  classifyOverlayAreaOverlapScope,
  getOverlayAreaOverlapCombineResult,
  OverlayAreaMetricValue,
  OverlayAreaOverlapCombineResult,
  attachRasterOverlayAreaOverlapScope,
  RasterOverlayAreaMetric,
} from "overlay-engine";
import { AnyLayer } from "mapbox-gl";
import {
  GeostatsLayer,
  isRasterInfo,
  RasterInfo,
} from "@seasketch/geostats-types";
import {
  CompatibleSpatialMetricDetailsFragment,
  OverlaySourceDetailsFragment,
  SpatialMetricState,
} from "../../generated/graphql";
import {
  extractColorForLayers,
  extractColorsForCategories,
  extractPaletteColorsFromVectorStyle,
  isTransparentColor,
} from "../utils/colors";
import {
  extractRasterLegendLabels,
  labelFromRasterLegendLabels,
} from "../../dataLayers/rasterLegendLabel";

export type ClassTableRow = {
  key: string;
  label: string;
  groupByKey: string;
  sourceId: string;
  color?: string;
  /** For vector or single-color raster; use with a single swatch. */
  stableId?: string;
  /**
   * For rasters: color stops from raster-color. For vectors: distinct colors
   * from data-driven fill/circle/line-color when a single row color cannot be resolved.
   */
  colors?: string[];
  /**
   * Multi-color 6×6 pixel swatch: ramp order for continuous raster ramps, soft
   * scatter for categorical rasters and vector palettes.
   */
  multiColorSwatchLayout?: "raster-ramp-order" | "soft-scatter";
};

/**
 * True when the overlay source is a raster dataset (geostats has bands, not vector layers).
 */
export function isRasterSource(
  source: OverlaySourceDetailsFragment
): source is OverlaySourceDetailsFragment & { geostats: { bands: unknown[] } } {
  const g = source?.geostats;
  return (
    typeof g === "object" &&
    g !== null &&
    "bands" in g &&
    Array.isArray((g as { bands: unknown[] }).bands) &&
    (g as { bands: unknown[] }).bands.length >= 1
  );
}

/**
 * Look up a geostats attribute by its column name (`attribute`), never by
 * position in the `attributes` array. Index-based references are invalid.
 */
export function findGeostatsAttributeByName(
  layer: Pick<GeostatsLayer, "attributes"> | null | undefined,
  name: string | undefined | null
) {
  if (!layer?.attributes?.length || typeof name !== "string" || name === "") {
    return undefined;
  }
  return layer.attributes.find((a) => a.attribute === name);
}

/**
 * Pixel-class keys usable as Raster Area Captured groupBy "value" rows.
 * Prefers RasterInfo categories, then legend labels, then opaque style stops
 * (minus `s:excluded`).
 */
export function getRasterOverlayAreaClassValues(
  source: Pick<OverlaySourceDetailsFragment, "geostats" | "mapboxGlStyles">
): string[] {
  const styles = source.mapboxGlStyles as AnyLayer[] | undefined;
  const excluded = getRasterExcludedValuesFromStyle(styles);
  const values: string[] = [];
  const push = (key: string) => {
    if (!key || excluded.has(key) || values.includes(key)) return;
    values.push(key);
  };

  if (isRasterInfo(source.geostats)) {
    const categoryBuckets = source.geostats.bands?.[0]?.stats?.categories ?? [];
    for (const bucket of categoryBuckets) {
      if (!Array.isArray(bucket) || typeof bucket[0] !== "number") continue;
      push(String(Math.round(bucket[0])));
    }
  }

  if (values.length === 0) {
    for (const key of Object.keys(extractRasterLegendLabels(styles))) {
      push(key);
    }
  }

  if (values.length === 0) {
    for (const key of Object.keys(getRasterCategoryColorsFromStyle(styles))) {
      push(key);
    }
  }

  return values;
}

/**
 * True when the source has at least two distinct class values to break down.
 * Presence / single-class rasters (e.g. mangroves) return false.
 */
export function canRasterBreakdownByValue(
  source: Pick<OverlaySourceDetailsFragment, "geostats" | "mapboxGlStyles">
): boolean {
  return getRasterOverlayAreaClassValues(source).length >= 2;
}

/**
 * Default `groupBy` for Raster Area Captured — same role as
 * `styleGroupByColumn` for OverlappingAreasTable.
 *
 * Returns `"value"` only when a multi-class breakdown is available.
 */
export function defaultRasterOverlayAreaGroupBy(
  source: Pick<OverlaySourceDetailsFragment, "geostats" | "mapboxGlStyles">
): "value" | undefined {
  return canRasterBreakdownByValue(source) ? "value" : undefined;
}

type RasterColorsFromStyle = {
  colors: string[];
  multiColorSwatchLayout: "raster-ramp-order" | "soft-scatter";
};

/**
 * Per-value colors from a categorical raster-color step/match expression.
 * Keys are stringified stop values (matching groupBy "value" class keys).
 */
export function getRasterCategoryColorsFromStyle(
  layers: AnyLayer[] | undefined
): { [value: string]: string } {
  const out: { [value: string]: string } = {};
  if (!layers?.length) {
    return out;
  }
  for (const layer of layers) {
    if (layer.type !== "raster" || !layer.paint) continue;
    const rasterColor = (layer.paint as Record<string, unknown>)[
      "raster-color"
    ];
    if (!Array.isArray(rasterColor) || rasterColor.length < 3) continue;
    const fn = rasterColor[0];
    if (fn === "step") {
      // step: ["step", input, default, stop1, color1, stop2, color2, ...]
      for (let i = 3; i < rasterColor.length; i += 2) {
        const stop = rasterColor[i];
        const color = rasterColor[i + 1];
        if (
          typeof stop === "number" &&
          typeof color === "string" &&
          !isTransparentColor(color)
        ) {
          out[String(Math.round(stop))] = color;
        }
      }
    } else if (fn === "match") {
      let i = 2;
      while (i < rasterColor.length) {
        if (i === rasterColor.length - 1) break;
        const input = rasterColor[i];
        const color = rasterColor[i + 1];
        if (
          typeof color === "string" &&
          !isTransparentColor(color) &&
          (typeof input === "number" || typeof input === "string")
        ) {
          const key =
            typeof input === "number"
              ? String(Math.round(input))
              : String(input);
          out[key] = color;
        }
        i += 2;
      }
    }
  }
  return out;
}

/** Values listed in style metadata `s:excluded` (skipped as table rows by default). */
export function getRasterExcludedValuesFromStyle(
  layers: AnyLayer[] | undefined
): Set<string> {
  const excluded = new Set<string>();
  if (!layers?.length) {
    return excluded;
  }
  for (const layer of layers) {
    const raw = (layer as { metadata?: { [k: string]: unknown } })?.metadata?.[
      "s:excluded"
    ];
    if (!Array.isArray(raw)) continue;
    for (const v of raw) {
      if (typeof v === "number" && Number.isFinite(v)) {
        excluded.add(String(Math.round(v)));
      } else if (typeof v === "string" && v.length > 0) {
        excluded.add(v);
      }
    }
  }
  return excluded;
}

/**
 * Extracts color values from raster-color. Interpolate/step → ramp order for
 * pixel swatches; match → soft scatter like categorical vectors.
 */
function getRasterColorsFromStyle(
  layers: AnyLayer[]
): RasterColorsFromStyle | undefined {
  for (const layer of layers) {
    if (layer.type !== "raster" || !layer.paint) continue;
    const rasterColor = (layer.paint as Record<string, unknown>)[
      "raster-color"
    ];
    if (!Array.isArray(rasterColor) || rasterColor.length < 3) continue;
    const fn = rasterColor[0];
    if (typeof fn !== "string") continue;

    const colorStops: string[] = [];

    const pushIfOpaque = (c: string) => {
      if (typeof c === "string" && !isTransparentColor(c)) colorStops.push(c);
    };

    let layout: "raster-ramp-order" | "soft-scatter";

    if (/^interpolate(-hcl|-lab)?$/.test(fn)) {
      layout = "raster-ramp-order";
      for (let i = 4; i < rasterColor.length; i += 2) {
        pushIfOpaque(rasterColor[i] as string);
      }
    } else if (fn === "step") {
      layout = "raster-ramp-order";
      for (let i = 2; i < rasterColor.length; i += 2) {
        pushIfOpaque(rasterColor[i] as string);
      }
    } else if (fn === "match") {
      layout = "soft-scatter";
      let i = 2;
      while (i < rasterColor.length) {
        if (i === rasterColor.length - 1) {
          pushIfOpaque(rasterColor[i] as string);
          break;
        }
        pushIfOpaque(rasterColor[i + 1] as string);
        i += 2;
      }
    } else {
      continue;
    }

    if (colorStops.length > 0) {
      return { colors: colorStops, multiColorSwatchLayout: layout };
    }
  }
  return undefined;
}

/**
 * Resolves a solid paint color when possible; otherwise distinct colors from
 * data-driven vector paint (match / step / interpolate) for a multi-stop swatch.
 */
function vectorSwatchFromSource(
  source: OverlaySourceDetailsFragment,
  singleColor: string | undefined
): {
  color?: string;
  colors?: string[];
  multiColorSwatchLayout?: "raster-ramp-order" | "soft-scatter";
} {
  const styles = source.mapboxGlStyles as AnyLayer[] | undefined;
  if (!styles?.length) {
    if (singleColor !== undefined && !isTransparentColor(singleColor)) {
      return { color: singleColor };
    }
    return {};
  }
  let color = singleColor;
  if (color !== undefined && isTransparentColor(color)) {
    color = undefined;
  }
  if (color !== undefined) {
    return { color };
  }
  const palette = extractPaletteColorsFromVectorStyle(styles);
  if (!palette?.length) {
    return {};
  }
  if (palette.length === 1) {
    return { color: palette[0] };
  }
  return { colors: palette, multiColorSwatchLayout: "soft-scatter" };
}

export type ClassTableRowComponentSettings = {
  /**
   * When true, row labels wrap instead of truncating with an ellipsis (default is truncated).
   */
  disableRowLabelTruncation?: boolean;
  /**
   * A list of row keys to exclude from the table. The key must match the ClassTableRow.key value.
   */
  excludedRowKeys?: string[];
  /**
   * A map of row keys to custom labels. The key must match the ClassTableRow.key value.
   */
  customRowLabels?: { [key: string]: string };
  /**
   * A map of row keys to stable IDs. The key must match the ClassTableRow.key value.
   */
  rowLinkedStableIds?: { [key: string]: string };
  /**
   * Overlay source stableIds for which an extra row should be shown for the
   * total across all features (`groupByKey` `"*"`). When `groupBy` is set, the
   * overlay engine still records combined overlap under `"*"` alongside per-class
   * keys; this setting exposes that as a table row.
   */
  includeAllFeaturesRowForGroupedSources?: string[];
};

/** Default: row labels are truncated to a single line unless `disableRowLabelTruncation` is set. */
export function shouldTruncateClassTableRowLabels(
  settings: Pick<ClassTableRowComponentSettings, "disableRowLabelTruncation">
): boolean {
  return !settings.disableRowLabelTruncation;
}

export function classTableRowKey(stableId: string, groupByKey?: string) {
  return `${stableId}-${groupByKey || "*"}`;
}

export function resolveClassTableRowStableId(
  row: Pick<ClassTableRow, "stableId" | "key" | "groupByKey">,
  linkedStableIds?: { [key: string]: string }
) {
  return (
    row.stableId ||
    linkedStableIds?.[row.key] ||
    (row.groupByKey ? linkedStableIds?.[row.groupByKey] : undefined)
  );
}

export function hasClassTableRowVisibilityToggle(
  rows: Pick<ClassTableRow, "stableId" | "key" | "groupByKey">[],
  linkedStableIds?: { [key: string]: string }
) {
  return rows.some((row) =>
    Boolean(resolveClassTableRowStableId(row, linkedStableIds))
  );
}

/**
 * Returns a list of ClassTableRows for the given dependencies and sources. In
 * report widgets like FeatureCountTable and OverlappingAreasTable, the widget
 * can populate these rows with metrics data for rendering as part of a table
 * component. The purpose of this function is to let table widgets delegate
 * responsibility for determining what rows to display, their colors, and their
 * labels, based on depedencies and common configuration. Widget-specific
 * functionality can then be implemented by callers.
 *
 * If sources aren't provided, likely because they haven't been loaded yet, the
 * function will return placeholder rows.
 * @param options
 * @returns
 */
export function getClassTableRows(options: {
  dependencies: MetricDependency[];
  sources: OverlaySourceDetailsFragment[];
  allFeaturesLabel: string;
  /**
   * A map of group by keys to custom labels. The key must match the
   * ClassTableRow.key value.
   */
  customLabels?: { [key: string]: string };
  /**
   * A map of table of contents item IDs to stable IDs for showing map layer
   * toggles. The key must match the ClassTableRow.sourceId value.
   */
  stableIds?: { [key: string]: string };
  excludedRowKeys?: string[];
  /** @see ClassTableRowComponentSettings.includeAllFeaturesRowForGroupedSources */
  includeAllFeaturesRowForGroupedSources?: string[];
}): ClassTableRow[] {
  const rows = [] as ClassTableRow[];
  const fragmentDependencies = options.dependencies.filter(
    (d) => d.subjectType === "fragments" && Boolean(d.stableId)
  );
  const multiSource =
    fragmentDependencies.length > 1 && options.sources.length > 1;
  for (const dependency of fragmentDependencies) {
    const source = options.sources.find(
      (s) => s.stableId === dependency.stableId
    );
    const isRaster = source ? isRasterSource(source) : false;
    const vectorLayer = source?.geostats?.layers?.[0] as
      | GeostatsLayer
      | undefined;
    const rasterInfo =
      source && isRaster && isRasterInfo(source.geostats)
        ? (source.geostats as RasterInfo)
        : undefined;
    const layer = isRaster ? undefined : vectorLayer;

    // Categorical raster groupBy ("value"): build rows from class values
    // even when the vector layer path would find nothing.
    if (
      source &&
      isRaster &&
      dependency.parameters?.groupBy === "value" &&
      rasterInfo
    ) {
      const styles = source.mapboxGlStyles as AnyLayer[] | undefined;
      const legendLabels = extractRasterLegendLabels(styles);
      const categoryColors = getRasterCategoryColorsFromStyle(styles);
      const values = getRasterOverlayAreaClassValues(source);
      const includeAllFeaturesRow =
        options.includeAllFeaturesRowForGroupedSources?.includes(
          dependency.stableId!
        );

      // No discoverable classes (presence / continuous) — keep a total row so
      // enabling groupBy never blanks the settings popover or table.
      if (values.length === 0) {
        const totalKey = classTableRowKey(dependency.stableId!, "*");
        const rasterSwatch = styles?.length
          ? getRasterColorsFromStyle(styles)
          : undefined;
        rows.push({
          key: totalKey,
          label:
            options.customLabels?.[totalKey] ||
            source.tableOfContentsItem?.title ||
            options.allFeaturesLabel,
          groupByKey: "*",
          sourceId: dependency.stableId!.toString(),
          stableId: options.stableIds?.[totalKey],
          ...(rasterSwatch
            ? {
                colors: rasterSwatch.colors,
                multiColorSwatchLayout: rasterSwatch.multiColorSwatchLayout,
              }
            : {}),
        });
        continue;
      }

      if (includeAllFeaturesRow) {
        const totalKey = classTableRowKey(dependency.stableId!, "*");
        const rasterSwatch = styles?.length
          ? getRasterColorsFromStyle(styles)
          : undefined;
        rows.push({
          key: totalKey,
          label:
            options.customLabels?.[totalKey] ||
            source.tableOfContentsItem?.title ||
            options.allFeaturesLabel,
          groupByKey: "*",
          sourceId: dependency.stableId!.toString(),
          stableId: options.stableIds?.[totalKey],
          ...(rasterSwatch
            ? {
                colors: rasterSwatch.colors,
                multiColorSwatchLayout: rasterSwatch.multiColorSwatchLayout,
              }
            : {}),
        });
      }
      for (const value of values) {
        const key = classTableRowKey(dependency.stableId!, value);
        const color = categoryColors[value];
        const rowSwatch =
          color !== undefined && !isTransparentColor(color) ? { color } : {};
        const numericValue = Number(value);
        rows.push({
          key,
          label:
            options.customLabels?.[key] ||
            (Number.isFinite(numericValue)
              ? labelFromRasterLegendLabels(legendLabels, numericValue)
              : legendLabels[value] || value),
          groupByKey: value,
          sourceId: dependency.stableId!.toString(),
          stableId: options.stableIds?.[key],
          ...rowSwatch,
        });
      }
      continue;
    }

    if (!source || !layer) {
      if (dependency.parameters?.groupBy) {
        [1, 2, 3].forEach((i) => {
          rows.push({
            // eslint-disable-next-line i18next/no-literal-string
            key: `${dependency.stableId}-placeholder-${i}`,
            label: `-`,
            // eslint-disable-next-line i18next/no-literal-string
            groupByKey: `${dependency.stableId}-placeholder-${i}`,
            sourceId: dependency.stableId!.toString(),
          });
        });
      } else {
        const key = classTableRowKey(dependency.stableId!, "*");
        const styles = source?.mapboxGlStyles as AnyLayer[] | undefined;
        let swatch: {
          color?: string;
          colors?: string[];
          multiColorSwatchLayout?: "raster-ramp-order" | "soft-scatter";
        } = {};

        if (source && isRasterSource(source) && styles?.length) {
          const raster = getRasterColorsFromStyle(styles);
          if (raster?.colors.length) {
            swatch = {
              colors: raster.colors,
              multiColorSwatchLayout: raster.multiColorSwatchLayout,
            };
          } else {
            swatch = vectorSwatchFromSource(
              source,
              extractColorForLayers(styles)
            );
          }
        } else if (source && styles?.length) {
          swatch = vectorSwatchFromSource(
            source,
            extractColorForLayers(styles)
          );
        }

        rows.push({
          key,
          label:
            options.customLabels?.[key] ||
            source?.tableOfContentsItem?.title ||
            options.allFeaturesLabel,
          groupByKey: "*",
          sourceId: dependency.stableId!.toString(),
          stableId: options.stableIds?.[key],
          ...swatch,
        });
      }
    } else {
      const groupBy = dependency.parameters?.groupBy;
      const attr = groupBy
        ? findGeostatsAttributeByName(layer, groupBy)
        : undefined;
      if (groupBy && !attr) {
        throw new Error(`Attribute ${groupBy} not found in geostats layer`);
      }
      if (groupBy && attr) {
        const values = Object.keys(attr.values || {});
        const colors = extractColorsForCategories(
          values,
          attr,
          source.mapboxGlStyles as AnyLayer[]
        );
        const includeAllFeaturesRow =
          options.includeAllFeaturesRowForGroupedSources?.includes(
            dependency.stableId!
          );
        if (includeAllFeaturesRow) {
          const totalKey = classTableRowKey(dependency.stableId!, "*");
          const totalSwatch = vectorSwatchFromSource(
            source,
            extractColorForLayers(source.mapboxGlStyles as AnyLayer[])
          );
          rows.push({
            key: totalKey,
            label:
              options.customLabels?.[totalKey] ||
              source.tableOfContentsItem?.title ||
              options.allFeaturesLabel,
            groupByKey: "*",
            sourceId: dependency.stableId!.toString(),
            stableId: options.stableIds?.[totalKey],
            ...totalSwatch,
          });
        }
        for (const value of values) {
          const key = classTableRowKey(dependency.stableId!, value);
          // Only use a category-specific color. Falling back to
          // extractColorForLayers(all layers) incorrectly paints every
          // unmatched category with the first fill-color in the style.
          const resolved = colors[value];
          const rowSwatch =
            resolved !== undefined && !isTransparentColor(resolved)
              ? { color: resolved }
              : {};
          rows.push({
            key,
            label: options.customLabels?.[key] || value,
            groupByKey: value,
            sourceId: dependency.stableId!.toString(),
            stableId: options.stableIds?.[key],
            ...rowSwatch,
          });
        }
      } else {
        const key = classTableRowKey(dependency.stableId!, "*");
        const swatch = vectorSwatchFromSource(
          source,
          extractColorForLayers(source.mapboxGlStyles as AnyLayer[])
        );
        rows.push({
          key,
          label:
            options.customLabels?.[key] ||
            (multiSource
              ? source.tableOfContentsItem?.title || options.allFeaturesLabel
              : options.allFeaturesLabel),
          groupByKey: "*",
          sourceId: dependency.stableId!.toString(),
          stableId: options.stableIds?.[key],
          ...swatch,
        });
      }
    }
  }
  return rows.filter((r) => !options.excludedRowKeys?.includes(r.key));
}

/**
 * Fragment metrics tagged with `geographyId` (`subject.geographies`).
 */
export function fragmentMetricsTaggedWithGeography(
  metrics: CompatibleSpatialMetricDetailsFragment[],
  geographyId: number,
  metricType?: string
): CompatibleSpatialMetricDetailsFragment[] {
  return metrics.filter((m) => {
    if (metricType && m.type !== metricType) {
      return false;
    }
    if (!subjectIsFragment(m.subject)) {
      return false;
    }
    return m.subject.geographies.includes(geographyId);
  });
}

/**
 * Combine complete metrics per overlay source for one geography.
 *
 * Fragment metrics are filtered to those tagged with `geographyId`
 * (see `subject.geographies`) before summing. The geography-subject metric
 * for that same id is returned alongside.
 */
export function combineMetricsBySource<T extends Metric>(
  metrics: CompatibleSpatialMetricDetailsFragment[],
  sources: OverlaySourceDetailsFragment[],
  geographyId: number,
  expectedMetricType?: Metric["type"]
): {
  [sourceId: string]: {
    fragments: T;
    geographies: T;
  };
} {
  // Keep a single complete metric per id while preserving metrics without ids.
  const dedupedMetrics: CompatibleSpatialMetricDetailsFragment[] = [];
  const seenMetricIds = new Set<string | number>();
  for (const metric of metrics) {
    if (metric.state !== SpatialMetricState.Complete) {
      continue;
    }
    if (metric.id === null || metric.id === undefined) {
      dedupedMetrics.push(metric);
      continue;
    }
    if (seenMetricIds.has(metric.id)) {
      continue;
    }
    seenMetricIds.add(metric.id);
    dedupedMetrics.push(metric);
  }

  const result: {
    [sourceId: string]: {
      fragments: T;
      geographies: T;
    };
  } = {};
  // first, gather up source ids
  const sourceIds = new Set<string>();
  for (const metric of dedupedMetrics) {
    if (metric.sourceUrl) {
      const source = sources.find((s) => s.sourceUrl === metric.sourceUrl);
      if (source) {
        sourceIds.add(source.stableId);
      }
    }
  }
  // then for each sourceId, combine the metrics
  for (const sourceId of sourceIds) {
    const source = sources.find((s) => s.stableId === sourceId);
    if (source) {
      const sourceMetrics = dedupedMetrics.filter(
        (m) =>
          m.sourceUrl === source.sourceUrl &&
          (!expectedMetricType || m.type === expectedMetricType)
      );
      const geographyMetrics = sourceMetrics.filter(
        (m) => subjectIsGeography(m.subject) && m.subject.id === geographyId
      );
      const geographyMetric =
        geographyMetrics.length > 1
          ? geographyMetrics
              .slice()
              .sort((a, b) => Number(b.id) - Number(a.id))[0]
          : geographyMetrics[0];
      const fragmentMetrics = fragmentMetricsTaggedWithGeography(
        sourceMetrics,
        geographyId
      );
      let fragments = combineMetricsForFragments(
        fragmentMetrics as Pick<Metric, "type" | "value">[],
        expectedMetricType
      ) as T;

      // Attach within/between-sketch scope onto buffered overlay_area combine
      // metadata when present. @see OverlayAreaOverlapInfo
      if (
        expectedMetricType === "overlay_area" ||
        fragmentMetrics.some((m) => m.type === "overlay_area")
      ) {
        fragments = attachOverlayAreaOverlapScope(
          fragments,
          fragmentMetrics
        ) as T;
      }

      if (
        expectedMetricType === "raster_overlay_area" ||
        fragmentMetrics.some((m) => m.type === "raster_overlay_area")
      ) {
        fragments = attachRasterOverlayAreaOverlapScope(
          fragments as Pick<RasterOverlayAreaMetric, "type" | "value">,
          fragmentMetrics
        ) as T;
      }

      result[source.stableId] = {
        fragments,
        geographies: geographyMetric as unknown as T,
      };
    }
  }
  return result;
}

/**
 * Enriches a combined overlay_area metric's `__overlap` result with
 * within-sketch / between-sketches scope derived from fragment subjects.
 *
 * @see OverlayAreaOverlapInfo
 * @see classifyOverlayAreaOverlapScope
 */
export function attachOverlayAreaOverlapScope<T extends Metric>(
  combined: Pick<T, "type" | "value">,
  fragmentMetrics: { type?: string | null; subject?: unknown }[]
): Pick<T, "type" | "value"> {
  if (combined.type !== "overlay_area") {
    return combined;
  }
  const value = combined.value as OverlayAreaMetricValue;
  const overlap = getOverlayAreaOverlapCombineResult(value);
  if (!overlap) {
    return combined;
  }
  const withSubjects = fragmentMetrics.filter(
    (m): m is { type?: string | null; subject: Metric["subject"] } =>
      m.subject != null
  );
  const scopeInfo = classifyOverlayAreaOverlapScope(withSubjects);
  const enriched: OverlayAreaOverlapCombineResult = {
    ...overlap,
    scope: scopeInfo.scope,
    partnerSketchIds: scopeInfo.partnerSketchIds,
    fragmentsInvolved: scopeInfo.fragmentsInvolved,
  };
  return {
    ...combined,
    value: {
      ...value,
      __overlap: enriched,
    },
  };
}
